import { AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/admin/shared/badge'
import { Modal } from '@/components/admin/shared/modal'
import type { ProcessingType } from './_lib/processing-trigger-policy'

const PROCESSING_LABEL: Record<ProcessingType, string> = {
  none: 'None',
  process: 'Process',
  reprocess: 'Reprocess',
  full_reprocess: 'Full reprocess',
}

const PROCESSING_BADGE: Record<ProcessingType, string> = {
  none: 'muted',
  process: 'active',
  reprocess: 'Medium',
  full_reprocess: 'Hard',
}

interface ReprocessConfirmModalProps {
  isOpen: boolean
  pendingCount: number
  summaryLabel: string
  processingTypes: ProcessingType[]
  onClose: () => void
  onProceed: () => void
}

export function ReprocessConfirmModal({
  isOpen,
  pendingCount,
  summaryLabel,
  processingTypes,
  onClose,
  onProceed,
}: ReprocessConfirmModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirm processing run">
      <div
        className="space-y-3"
        role="dialog"
        aria-label="Processing confirmation"
      >
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <AlertTriangle
            size={16}
            className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
          />
          <p className="text-sm">
            This will kick off recommendation processing for{' '}
            <strong>{pendingCount}</strong> pending edit
            {pendingCount === 1 ? '' : 's'}.
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-foreground text-sm font-medium">Jobs to run</p>
          <div className="flex flex-wrap gap-2">
            {processingTypes.map((type) => (
              <Badge key={type} variant={PROCESSING_BADGE[type]}>
                {PROCESSING_LABEL[type]}
              </Badge>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">{summaryLabel}</p>
        </div>
        <div className="border-border flex justify-end gap-2 border-t pt-3">
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md px-3 py-1.5 text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onProceed}
            className="bg-primary text-primary-foreground rounded-md px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90"
          >
            Proceed
          </button>
        </div>
      </div>
    </Modal>
  )
}
